/**
 * Contratos de entrada (Zod) para crear y editar productos de crédito.
 */
import { z } from 'zod';
import { PAYMENT_FREQUENCIES } from '../credit/frequency.js';
import { DEFAULT_LATE_GRACE_DAYS, DEFAULT_MAX_ACTIVE_LOANS_PER_CLIENT, DEFAULT_MIN_COLLATERAL_COVERAGE_BP, GUARANTEE_TYPES, LOAN_PURPOSES, MAX_ACTIVE_LOANS_PER_CLIENT_LIMIT, MAX_LATE_GRACE_DAYS, MAX_PRINCIPAL_GRACE_PERIODS, MAX_TERM_PERIODS, } from '../credit/product.js';
import { DEFAULT_REPAYMENT_METHOD, REPAYMENT_METHODS } from '../credit/repayment.js';
import { INTEREST_METHODS } from '../credit/schedule.js';
import { basisPointsSchema, moneySchema, optionalText, TEXT_MAX, positiveMoneySchema } from './common.js';
/** Código de producto: 2 a 12 letras mayúsculas, dígitos o guiones (p. ej. "COM-SEM"). */
const PRODUCT_CODE_PATTERN = /^[A-Z0-9-]{2,12}$/;
/** Campos editables de un producto de crédito. */
const productFields = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(PRODUCT_CODE_PATTERN, 'Código de 2 a 12 caracteres: letras, números o guiones'),
    name: z.string().trim().min(3).max(TEXT_MAX.personName),
    description: optionalText(TEXT_MAX.notes),
    isActive: z.boolean().default(true),
    interestMethod: z.enum(INTEREST_METHODS),
    repaymentMethods: z
        .array(z.enum(REPAYMENT_METHODS))
        .min(1, 'Selecciona al menos una forma de pago')
        .default([DEFAULT_REPAYMENT_METHOD]),
    frequencies: z.array(z.enum(PAYMENT_FREQUENCIES)).min(1, 'Selecciona al menos una frecuencia'),
    purposes: z.array(z.enum(LOAN_PURPOSES)).min(1),
    guaranteeTypes: z.array(z.enum(GUARANTEE_TYPES)).min(1),
    minAmount: positiveMoneySchema,
    maxAmount: positiveMoneySchema,
    minTermPeriods: z.number().int().min(1).max(MAX_TERM_PERIODS),
    maxTermPeriods: z.number().int().min(1).max(MAX_TERM_PERIODS),
    minAnnualRate: basisPointsSchema,
    maxAnnualRate: basisPointsSchema,
    defaultAnnualRate: basisPointsSchema,
    maxPrincipalGracePeriods: z.number().int().min(0).max(MAX_PRINCIPAL_GRACE_PERIODS).default(0),
    originationFeeRate: basisPointsSchema.default(0),
    fixedCharges: moneySchema.default(0),
    /** IVA sobre intereses de este producto; `null` usa el valor de la organización. */
    taxOnInterest: basisPointsSchema.nullable().default(null),
    shiftToBusinessDay: z.boolean().default(true),
    lateInterestRate: basisPointsSchema,
    lateGraceDays: z.number().int().min(0).max(MAX_LATE_GRACE_DAYS).default(DEFAULT_LATE_GRACE_DAYS),
    lateFixedFee: moneySchema.default(0),
    minCollateralCoverage: basisPointsSchema.default(DEFAULT_MIN_COLLATERAL_COVERAGE_BP),
    requiresGuarantor: z.boolean().default(false),
    maxActiveLoansPerClient: z
        .number()
        .int()
        .min(1)
        .max(MAX_ACTIVE_LOANS_PER_CLIENT_LIMIT)
        .default(DEFAULT_MAX_ACTIVE_LOANS_PER_CLIENT),
});
/** Verifica que cada máximo sea ≥ a su mínimo y que la tasa sugerida esté dentro del rango. */
function productRangesAreConsistent(p, ctx) {
    const ranges = [
        [p.minAmount, p.maxAmount, 'maxAmount', 'El monto máximo debe ser mayor o igual al mínimo'],
        [p.minTermPeriods, p.maxTermPeriods, 'maxTermPeriods', 'El plazo máximo debe ser mayor o igual al mínimo'],
        [p.minAnnualRate, p.maxAnnualRate, 'maxAnnualRate', 'La tasa máxima debe ser mayor o igual a la mínima'],
    ];
    for (const [min, max, path, message] of ranges) {
        if (min !== undefined && max !== undefined && max < min) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
        }
    }
    const { defaultAnnualRate, minAnnualRate, maxAnnualRate } = p;
    if (defaultAnnualRate !== undefined &&
        minAnnualRate !== undefined &&
        maxAnnualRate !== undefined &&
        (defaultAnnualRate < minAnnualRate || defaultAnnualRate > maxAnnualRate)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['defaultAnnualRate'],
            message: 'La tasa sugerida debe estar dentro del rango',
        });
    }
}
/** Alta de un producto de crédito. */
export const CreditProductSchema = productFields.superRefine(productRangesAreConsistent);
/** Edición parcial de un producto de crédito. */
export const UpdateCreditProductSchema = productFields.partial().superRefine(productRangesAreConsistent);
//# sourceMappingURL=product.js.map